import React from 'react'

import { IsEmbedded, NotEmbedded } from '@sketch/components'

import { getVersionRenderStatus } from '@sketch/modules-common'
import { VersionFragment, useGetSectionsForPageQuery } from '@sketch/gql-types'

import { ShareWithoutVersion } from '../../../../versioning'
import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import { Panel } from '../../../components/Panel'
import { FloatingZoom } from '../../../FrameView/components/FloatingZoom'
import {
  CanvasMobileToolbar,
  FOCUS_VIEW_TOOLBAR_ITEMS,
} from '../../../components/CanvasMobileToolbar'
import { CanvasLeftPanelPortal } from '../../../../canvas/components/CanvasLeftPanel'
import { SectionSelector } from '../SectionSelector'
import {
  SectionDetailHeader,
  SectionDetailHeaderEmbedded,
} from '../SectionDetailHeader'

interface SectionViewProps {
  layoutProps: DocumentSidebarLayoutExtraProps
  share: ShareWithoutVersion
  currentVersion: VersionFragment | null
  pageUUID: string
  sectionUUID: string
  isViewingLatestVersion: boolean
  renderStatus: ReturnType<typeof getVersionRenderStatus>
}

/**
 * Layout for the single-Section focus view - the Section equivalent of
 * FrameView. The canvas itself is the shared CanvasSurface, which isolates the
 * Section by making it the renderer's scene root; only the header and the
 * panel's navigation differ from the plain page view.
 */
export const SectionView = ({
  layoutProps,
  share,
  currentVersion,
  pageUUID,
  sectionUUID,
  isViewingLatestVersion,
  renderStatus,
}: SectionViewProps) => {
  const {
    HeaderPortal,
    SidebarRightPortal,
    toggleSidebarRight,
    isSidebarRightOpen,
  } = layoutProps

  // GQL-backed, so this resolves independently of the WASM engine parsing
  // the presentation file - the header can populate as soon as the network
  // responds, instead of waiting on usePRFileSections().
  const { data } = useGetSectionsForPageQuery({
    variables: {
      identifier: share.identifier,
      versionShortId: currentVersion?.shortId ?? '',
      pageUUID,
    },
    skip: !currentVersion?.shortId,
  })
  const page = data?.share?.version?.document?.page
  const currentSection = page?.sections.find(
    section => section.uuid === sectionUUID
  )

  // Always defined from data we already have synchronously (sectionUUID and
  // pageUUID both come from the URL) so the header renders immediately on
  // navigation instead of staying empty - name/pageName fill in once the
  // GQL query resolves, in place, without remounting anything.
  const sectionInfo = {
    uuid: sectionUUID,
    name: currentSection?.name ?? '',
    pageUUID,
    pageName: page?.name ?? '',
  }

  return (
    <>
      <HeaderPortal>
        {/* An embed gets a header of its own, as the page and frame views have
        always had: the Section you are in, the ones beside it, and the way back
        to the document. The panels behind the full header - versions, inspect,
        comments - have nowhere to open inside an embed. */}
        <NotEmbedded>
          <SectionDetailHeader
            share={share}
            sectionInfo={sectionInfo}
            toggleSidebarRight={toggleSidebarRight}
            isSidebarRightOpen={isSidebarRightOpen}
            isViewingLatestVersion={isViewingLatestVersion}
            userCanOpenInApp={!!currentVersion?.document?.userCanOpenInApp}
            currentVersion={currentVersion}
          />

          <Panel
            share={share}
            version={currentVersion || undefined}
            userCanSeeComments
            userCanInspect={share.userCanInspect}
            SidebarRightPortal={SidebarRightPortal}
          />
        </NotEmbedded>

        <IsEmbedded>
          <SectionDetailHeaderEmbedded
            share={share}
            sectionInfo={sectionInfo}
          />
        </IsEmbedded>

        <FloatingZoom />
      </HeaderPortal>

      {/* The canvas, and the guard that stands in for it while the document
      is not ready, are mounted once by ShareCanvasRoute. The selector is a
      portal into the panel beside it, so it finds no host and renders nothing
      while the guard is up; the toolbar is not, so it asks. */}
      <CanvasLeftPanelPortal>
        <SectionSelector />
      </CanvasLeftPanelPortal>
      {renderStatus === 'ready' && (
        <CanvasMobileToolbar items={FOCUS_VIEW_TOOLBAR_ITEMS} />
      )}
    </>
  )
}
